import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FavoritesService } from '@/api/user-portal-service';
import { queryKeys } from '@/common/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { UserApplication } from '@/types/api.types';

interface ToggleFavoriteParams {
  applicationId: string;
  isFavorite: boolean;
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, ToggleFavoriteParams>({
    mutationFn: async ({ applicationId, isFavorite }) => {
      await FavoritesService.toggleFavorite(applicationId, {
        is_favorite: isFavorite,
      });
    },
    onMutate: async ({ applicationId, isFavorite }) => {
      const queryKey = queryKeys.applications.list();
      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueryData<UserApplication[]>(queryKey, current =>
        current?.map(app =>
          app.app_id === applicationId
            ? { ...app, is_favorite: isFavorite }
            : app
        )
      );
    },
    onError: (error, { applicationId, isFavorite }) => {
      queryClient.setQueryData<UserApplication[]>(
        queryKeys.applications.list(),
        current =>
          current?.map(app =>
            app.app_id === applicationId
              ? { ...app, is_favorite: !isFavorite }
              : app
          )
      );
      toast.error({
        title: 'Unable to Update Favorite',
        message:
          error.message || 'Unable to update favorite. Please try again.',
      });
    },
  });
}
